import { defineComponent } from '@/components/base';
import { convertCamelCaseToWords } from '@/utils';
import ColorSelection from '@/components/mini-quiz/color-selection';
import ButtonsLayout from '@/components/mini-quiz/buttons-layout';
import Dropdown from '@/components/mini-quiz/dropdown';
import InformationPills from '@/components/mini-quiz/information-pills';

const KeyTitleMap = {
  hairTypes: 'Hair Type',
  hairThickness: 'Hair Thickness',
  scalpType: 'Scalp Moisture',
  stylingGoals: 'Styling Goals',
  hairGoals: 'Hair Goals',
  holdStrength: 'Hold Strength'
};

/**
 * @typedef {'circle' | 'button' | 'dropdown'} QuizLayoutKeys
 */
const QuizLayoutKeys = {
  Circle: 'circle',
  Button: 'button',
  Dropdown: 'dropdown',
  Pill: 'pill'
};

const QuizLayout = {
  [QuizLayoutKeys.Circle]: ({
    productHandle,
    excludedColors,
    excludedProductColorsVariantsFromMiniQuiz,
    colorOptions,
    colorOptionsJsonFormatted,
    quizType
  }) => {
    defineComponent('color-selection', ColorSelection);
    return `
          <h3>colors</h3>
          <color-selection
              excluded-colors="${
                (excludedColors || '') +
                ',' +
                (excludedProductColorsVariantsFromMiniQuiz || '')
              }"
              colors='${colorOptions}'
              product='${productHandle}'
              ${quizType == 'bundle' ? colorOptionsJsonFormatted : '[]'}>
          </color-selection>
          `;
  },
  [QuizLayoutKeys.Button]: (optionData) => {
    defineComponent('buttons-layout', ButtonsLayout);

    const keyName = KeyTitleMap[optionData.name] ?? optionData.name;
    const quantityToSelect = optionData.value[0]?.quantityToSelect || 1;
    const objectConfig = {
      keyName,
      quantityToSelect
    };

    return `
    <buttons-layout id="${optionData.name}SelectorButtons">
      <data name="options" type="json" >
        ${JSON.stringify(optionData?.value || [])}
      </data>
      <data name="config" type="json">
        ${JSON.stringify(objectConfig)}
      </data>
    </buttons-layout>
    `;
  },
  [QuizLayoutKeys.Dropdown]: (optionData) => {
    defineComponent('dropdown-component', Dropdown);
    const keyName = KeyTitleMap[optionData.name] ?? optionData.name;

    return `
      <h3>${convertCamelCaseToWords(keyName)}</h3>
      <dropdown-component id="${optionData.name}Selector">
        <data name="options" type="json">
          ${JSON.stringify(optionData.value)}
        </data>
      </dropdown-component>`;
  },
  [QuizLayoutKeys.Pill]: (optionData) => {
    defineComponent('information-pills', InformationPills);

    const keyName = KeyTitleMap[optionData.name] ?? optionData.name;
    const quantityToSelect = optionData.value[0]?.quantityToSelect || 1;
    const objectConfig = {
      keyName,
      quantityToSelect
    };

    return `
    <information-pills id="${optionData.name}SelectorButtons">
      <data name="options" type="json" >
        ${JSON.stringify(optionData?.value || [])}
      </data>
      <data name="config" type="json">
        ${JSON.stringify(objectConfig)}
      </data>
    </information-pills>
    `;
  }
};

/**
 * @typedef {Object} OptionData
 * @property {string[]} value
 * @property {QuizLayoutKeys} inputType
 */

/**
 * Render a quiz question based on its layout type and options.
 *
 * @param {OptionData} optionData - The data for the quiz option.
 * @returns {string | any} Rendered output or an empty string.
 */
const RenderQuizQuestion = (optionData) => {
  if (optionData.inputType === 'checkbox') {
    return optionData.value === true
      ? `
                  <h3>Do you prefer a silicone-free formula? <span id="ModalButton" style="cursor: pointer; text-decoration: underline;">(not sure?)</span></h3>
                    <label for="siliconeFreeCheckbox" class="checkbox-container">
                      <input type="checkbox" id="siliconeFreeCheckbox" name="siliconeFree" />
                      Yes, I prefer a silicone-free formula
                    </label>
                `
      : '';
  } else {
    return optionData.value.length > 0
      ? QuizLayout[optionData.inputType](optionData)
      : '';
  }
};

export { RenderQuizQuestion };
